import React, { useRef, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { RandomCompany } from '../types/randomCompanies';
import { useCompanyCardAnimation } from '../hooks/useCompanyCardAnimation';
import { CompanyCardHeader } from './CompanyCardHeader';
import { CompanyCardDetails } from './CompanyCardDetails';

interface CompanyVoteCardProps {
  company: RandomCompany;
  onVote: (companyId: string, companyName: string, vote: 'good' | 'bad', targetCoords?: { x: number, y: number }) => void;
  isPreRender?: boolean;
  isHighlighted?: boolean;
  onExpand?: (companyId: string) => void;
  onCloseOverlay?: () => void;
  onEdit?: (companyId: string) => void;
}

export const CompanyVoteCard: React.FC<CompanyVoteCardProps> = ({ 
  company, 
  onVote, 
  isPreRender = false,
  isHighlighted = false,
  onExpand,
  onCloseOverlay,
  onEdit
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  
  const {
    cardRef,
    isFlying,
    flyDirection,
    flyStyle,
    startFlyAnimation
  } = useCompanyCardAnimation({ companyId: company.id, isPreRender });
  
  const isNonOperating = company.is_operating === false || company.is_parked === true;
  const isVoted = !!company.voted;
  const isNewlyVoted = company.isNewlyVoted;
  const hasOverlay = company.hasOverlay;

  // Handle click outside for overlay
  useEffect(() => {
    if (hasOverlay && onCloseOverlay) {
      const handleClickOutside = (event: MouseEvent) => {
        if (overlayRef.current && !overlayRef.current.contains(event.target as Node)) {
          onCloseOverlay();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [hasOverlay, onCloseOverlay]);

  const handleVote = (vote: 'good' | 'bad') => {
    const targetCoords = startFlyAnimation(vote);
    onVote(company.id, company.business_name || company.name, vote, targetCoords || undefined);
  };

  const handleExpand = () => {
    if (onExpand) {
      onExpand(company.id);
    }
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit(company.id);
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Only expand if it's a voted card and not already expanded
    if (isVoted && !hasOverlay) {
      // Don't expand if clicking on buttons
      if ((e.target as HTMLElement).closest('button')) {
        return;
      }
      handleExpand();
    }
  };
  
  return (
    <>
      {/* Main Card */}
      <Card 
        ref={cardRef}
        data-company-id={isPreRender ? company.id : undefined}
        className={`border-slate-600 transition-all duration-500 ${
          isFlying 
            ? `animate-fly-${flyDirection} z-50 fixed`
            : isNonOperating 
              ? 'bg-slate-900 border-slate-800 opacity-50' 
              : isVoted
                ? `bg-slate-800 border-l-4 ${
                    company.voted === 'good' ? 'border-l-green-500' : 'border-l-red-500'
                  } ${
                    isNewlyVoted 
                      ? 'ring-2 ring-yellow-400 shadow-lg shadow-yellow-400/50 animate-bounce-land' 
                      : 'opacity-60 scale-95 cursor-pointer hover:opacity-80 hover:scale-100'
                  }`
                : 'bg-slate-700 hover:bg-slate-600'
        } ${isPreRender ? 'opacity-0 pointer-events-none' : ''} ${
          isHighlighted ? 'ring-2 ring-blue-400/50 shadow-lg shadow-blue-400/20' : ''
        }`}
        style={isFlying ? flyStyle : undefined}
        onClick={handleCardClick}
      >
        <CardContent className={`transition-all duration-500 ${isVoted ? 'p-2' : 'p-4'}`}>
          <div className="space-y-3">
            <CompanyCardHeader
              company={company}
              isNonOperating={isNonOperating}
              isVoted={isVoted}
              isExpanded={hasOverlay}
              onVote={handleVote}
              onExpand={handleExpand}
              onEdit={handleEdit}
            />

            {/* Show details for unvoted companies only (voted companies show details in overlay) */}
            {!isVoted && (
              <CompanyCardDetails
                company={company}
                isNonOperating={isNonOperating}
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Overlay Card */}
      {hasOverlay && (
        <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4">
          <Card 
            ref={overlayRef}
            className={`border-slate-600 bg-slate-800 border-l-4 ${
              company.voted === 'good' ? 'border-l-green-500' : 'border-l-red-500'
            } max-w-2xl w-full max-h-[80vh] overflow-y-auto`}
          >
            <CardContent className="p-6">
              <div className="space-y-4">
                <CompanyCardHeader
                  company={company}
                  isNonOperating={isNonOperating}
                  isVoted={isVoted}
                  isExpanded={true}
                  onVote={handleVote}
                  onExpand={onCloseOverlay}
                  onEdit={handleEdit}
                />

                <CompanyCardDetails
                  company={company}
                  isNonOperating={isNonOperating}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}; 